import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { useAqua } from '../lib/aquaStore';
import { linkColor } from '../hooks/useAquaLayer';
import { AquaLegend } from './AquaLegend';

const SAT = 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}';
const DEM = 'https://s3.amazonaws.com/elevation-tiles-prod/terrarium/{z}/{x}/{y}.png';
const fc = (s) => ({ type: 'FeatureCollection', features: s.res.links.filter((l) => l.kind === 'pipe').map((l) => ({ type: 'Feature', properties: { c: linkColor(s, l), dead: l.dead ? 1 : 0 }, geometry: { type: 'LineString', coordinates: l.coords.map((p) => [p[1], p[0]]) } })) });
const pts = (s) => ({ type: 'FeatureCollection', features: s.res.dry.map((p) => ({ type: 'Feature', properties: {}, geometry: { type: 'Point', coordinates: [p[1], p[0]] } })) });

// 3D view: network draped on the terrain, coloured by pressure / temperature / age / velocity
export default function Aqua3D({ t }) {
  const [on, setOn] = useState(false);
  const s = useAqua(), el = useRef(null), map = useRef(null);
  useEffect(() => { const f = () => setOn(true); window.addEventListener('giss-aqua-3d', f); return () => window.removeEventListener('giss-aqua-3d', f); }, []);
  useEffect(() => {
    if (!on || !s.res || s.res.error || !el.current) return undefined;
    const all = s.res.links.flatMap((l) => l.coords);
    const b = all.reduce((a, p) => [[Math.min(a[0][0], p[1]), Math.min(a[0][1], p[0])], [Math.max(a[1][0], p[1]), Math.max(a[1][1], p[0])]], [[180, 90], [-180, -90]]);
    const m = new maplibregl.Map({
      container: el.current, bounds: b, fitBoundsOptions: { padding: 80 }, pitch: 60, maxPitch: 80, attributionControl: { compact: true },
      style: { version: 8, sources: { sat: { type: 'raster', tiles: [SAT], tileSize: 256, maxzoom: 19, attribution: 'Esri World Imagery' }, dem: { type: 'raster-dem', tiles: [DEM], tileSize: 256, encoding: 'terrarium', maxzoom: 14 } },
        layers: [{ id: 'bg', type: 'background', paint: { 'background-color': '#dbe7f3' } }, { id: 'sat', type: 'raster', source: 'sat' }], terrain: { source: 'dem', exaggeration: 1.5 } },
    });
    m.addControl(new maplibregl.NavigationControl({ visualizePitch: true }));
    m.on('load', () => {
      m.addSource('net', { type: 'geojson', data: fc(s) });
      m.addSource('dry', { type: 'geojson', data: pts(s) });
      m.addLayer({ id: 'net-c', type: 'line', source: 'net', paint: { 'line-color': '#0f172a', 'line-width': 9, 'line-opacity': 0.5 }, layout: { 'line-cap': 'round', 'line-join': 'round' } });
      m.addLayer({ id: 'net', type: 'line', source: 'net', filter: ['!=', ['get', 'dead'], 1], paint: { 'line-color': ['get', 'c'], 'line-width': 6 }, layout: { 'line-cap': 'round', 'line-join': 'round' } });
      m.addLayer({ id: 'net-dead', type: 'line', source: 'net', filter: ['==', ['get', 'dead'], 1], paint: { 'line-color': ['get', 'c'], 'line-width': 6, 'line-dasharray': [1, 1.5] } });
      m.addLayer({ id: 'dry', type: 'circle', source: 'dry', paint: { 'circle-radius': 9, 'circle-color': 'rgba(220,38,38,0.25)', 'circle-stroke-color': '#dc2626', 'circle-stroke-width': 3 } });
    });
    map.current = m;
    return () => { m.remove(); map.current = null; };
  }, [on]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { const m = map.current; if (m?.getSource('net') && s.res && !s.res.error) { m.getSource('net').setData(fc(s)); m.getSource('dry').setData(pts(s)); } }, [s]);
  if (!on) return null;
  return createPortal(
    <div className="fly-overlay" data-testid="aqua-3d">
      <div ref={el} className="fly-map" />
      <div className="aq-3d-legend"><AquaLegend t={t} /></div>
      <button className="icon-btn aq-3d-close" data-testid="aqua-3d-close" onClick={() => setOn(false)}><X size={20} /></button>
    </div>,
    document.body,
  );
}
